import Vue from 'vue'
import Vuex from 'vuex';

Vue.use(Vuex);

// Add the modules in the store
import parseSwagger from './store/modules/auto-modules-swagger.js'
import moduleDelete from './store/api-store/module-delete.js'
import moduleList from './store/api-store/module-list.js'
import moduleShow from './store/api-store/module-show.js'
import moduleApi from './store/api-store/api-module.js'
import authentication from './store/api-store/authentication-module.js'
import subContext from './store/api-store/sub-context.js'
import moduleToasts from './store/api-store/module-toasts.js'
import buildModuleUpdate from './store/api-store/module/update.js'
import buildModuleCreate from './store/api-store/module/create.js'
import buildModuleCompositeUpdate from './store/api-store/module/composite-update.js'
import buildModuleCompositeCreate from './store/api-store/module/composite-create.js'

/**
 * @typedef ModuleBuilderContext
 *
 * @property {object} resource
 * @property {object} parsed
 * @property {string} $name
 * @property {string} $namespace
 * @property {string} $parentNamespace
 */

/**
 * Build the store modules.
 *
 * @param {object} parsed   The parsed API information.
 * @param {string} endpoint The API base endpoint.
 * @return {Promise<any>}
 */
function buildModules (parsed, endpoint) {

  function createSubmodule (resource) {
    const $name = resource.name
    const $namespace = resource.name
    const $parentNamespace = ''

    const modules = {};
    const context = {
      resource,
      parsed,
      $name: resource.name,
      $namespace: resource.name,
      $parentNamespace: '',
    };
    if (resource.operations.create) {
      const operation = resource.operations.create
      if (resource.name === 'invoice') {
        const prepareChild = (item, parent) => {
          item.invoice = parent['@id']
          return item
        }

        const childCreateUrl = parsed.invoicepos.operations.create.url
        const childCreateProperties = parsed.invoicepos.operations.create.input.properties
        const childUpdateProperties = parsed.invoicepos.operations.update.input.properties

        modules.create = buildModuleCompositeCreate(
            resource.name + '/create',
            operation.input.properties,
            operation.url,
            childCreateUrl,
            childCreateProperties,
            childUpdateProperties,
            'positions',
            parsed,
            prepareChild
        );
      } else {
        modules.create = buildModuleCreate(operation.input.properties, context.resource.operations.create.url, parsed);
      }
    }
    if (resource.operations.delete) {
      modules.del = moduleDelete(subContext(context, 'del'));
    }
    if (resource.operations.list) {
      modules.list = moduleList(subContext(context, 'list'));
    }

    if (resource.operations.show) {
      modules.show = moduleShow(subContext(context, 'show'));
    }
    if (resource.operations.update) {
      const operation = resource.operations.update
      if (resource.name === 'invoice') {
        const prepareChild = (item, parent) => {
          item.invoice = parent['@id']
            return item
        }

        const childCreateUrl = parsed.invoicepos.operations.create.url
        const childCreateProperties = parsed.invoicepos.operations.create.input.properties
        const childUpdateProperties = parsed.invoicepos.operations.update.input.properties

        modules.update = buildModuleCompositeUpdate(
          resource.name + '/update',
          operation.input.properties,
          childCreateUrl,
          childCreateProperties,
          childUpdateProperties,
          'positions',
          parsed,
          prepareChild
        );
      } else {
        modules.update = buildModuleUpdate(operation.input.properties, parsed)
      }
    }

    return {
      namespaced: true,
      state: {
        $name,
        $namespace,
        $parentNamespace,
      },
      modules
    }
  }

  return new Promise((resolve, reject) => {
    const result = {
      api: moduleApi(endpoint),
      authentication: authentication({resource: parsed.jwtoken, parsed}),
      toasts: moduleToasts()
    };
    Object.keys(parsed).map((resourceName) => {
      result[resourceName] = createSubmodule(parsed[resourceName]);
    })
    resolve(result)
  })
}

function build (entrypoint) {
  return new Promise((resolve) => {
    parseSwagger(entrypoint)
      .then((information) => {
        buildModules(information.parsed, information.entrypoint)
          .then((modules) => {
            const state = {
              locale: 'en',
              runningRequestCount: 0,
              apiEntrypoint: information.entrypoint,
              apiInformation: information.parsed
            }

            const store = new Vuex.Store({
              strict: process.env.NODE_ENV !== 'production',
              modules,
              state,
              actions: {
                startRequest ({commit}) {
                  commit('startRequest');
                },
                endRequest ({commit}) {
                  commit('endRequest');
                },
              },
              getters: {
                locale: (state) => state.locale,
                isLoading: (state) => {
                  return state.runningRequestCount > 0;
                },
                apiEntrypoint: state => state.apiEntrypoint,
                apiInformation: state => state.apiInformation,
              },
              mutations: {
                startRequest (state) {
                  state.runningRequestCount++;
                },
                endRequest (state) {
                  state.runningRequestCount--;
                }
              }
            });

            for (let [name, module] of Object.entries(store._modulesNamespaceMap)) {
              const namespace = name.slice(0, -1)

              const values = {
                $name: namespace.split('/').pop(),
                $namespace: namespace,
                $parentNamespace: namespace.split('/').slice(0, -1).join('/')
              }
              Object.keys(values).map((valName) => {
                const should = values[valName]
                if (module.context.state[valName] === undefined) {
                  console.warn(`Setting ${valName} to ${should}`)
                  module.context.state[valName] = should
                }
                if (module.context.state[valName] !== should) {
                  console.error(`${valName} is ${module.context.state[valName]} but should be ${should}`)
                }
              })

              module.watch && module.watch.map((watch) => {
                watch(store, namespace)
              })
            }

            resolve(store)
          })
      })
  })
}

export default build
